import { memo, useCallback, useMemo } from 'react'
import clsx from 'clsx'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import NestableClickable from 'components/Common/NestableClickable'
import { stopPropagation } from 'lib/web/eventHandlerUtils'
import { mapPlaceImageIds } from 'tripPlanner/utils/placeImages'
import {
  THINGS_TO_DO_DESCRIPTION_LINE_CLAMP,
  THINGS_TO_DO_TILE_CONTENT_GAP,
  THINGS_TO_DO_TILE_CONTENT_PADDING,
  thingsToDoPlaceTileImage,
  thingsToDoPlaceTileLayout,
} from './thingsToDoPlaceTileLayout'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import useImageCarouselHoverPreview from 'hooks/useImageCarouselHoverPreview'
import { useIsMobileOrTablet } from 'hooks/useIsMobileOrTablet'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import AddToTripAction from 'tripPlanner/components/GenericDrawer/AddToTripAction'
import GooglePlaceRatingDisplay from 'tripPlanner/components/GooglePlaceRating/GooglePlaceRatingDisplay'
import type { TripItineraryData } from 'tripPlanner/api/types'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import type { ExploreItem } from 'tripPlanner/types/common'
import { getPlaceDescription } from 'tripPlanner/utils/placeDescription'
import PlaceDescriptionAttribution from './PlaceDescriptionAttribution'

const TileContainer = styled(NestableClickable)`
  ${thingsToDoPlaceTileLayout}
`

const ImageSection = styled.div`
  ${thingsToDoPlaceTileImage}
`

/*
 * The carousel gives its image a height but no width, so without a declared aspect a portrait
 * photo widens the column it sits in.
 */
const PLACE_IMAGE_PARAMS: ImageParams = {
  quality: 'good',
  fit: 'center',
  gravity: 'center',
  mobileAspectRatio: '2:1',
  tabletAspectRatio: '2:1',
  desktopAspectRatio: '2:1',
  mobileWidth: '100vw',
  tabletWidth: '540px',
}

const AddToTripButtonContainer = styled.div`
  position: absolute;
  top: ${rem(12)};
  inset-inline-end: ${rem(12)};
  z-index: 1;
`

const ContentSection = styled(Group)`
  padding: ${rem(THINGS_TO_DO_TILE_CONTENT_PADDING)};
`

// `.length`, not `??`: an empty `imageIds` still has to fall through to the photo URL.
function getStoredPlaceImages(place: ExploreItem['place']): Array<App.Image> {
  const storedImages = mapPlaceImageIds(place.imageIds)
  if (storedImages.length > 0) return storedImages

  return place.photoUrl?.startsWith('https://') ? [{ url: place.photoUrl }] : []
}

interface Props {
  place: ExploreItem
  displayRegion?: string
  onSelectPlace: (place: ExploreItem) => void
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  onAfterAction?: () => void
}

function ThingsToDoPlaceTile({
  place,
  displayRegion,
  onSelectPlace,
  itineraryData,
  onSendMessage,
  onAfterAction,
}: Props) {
  const intl = useIntl()
  const handleSelectPlace = useCallback(() => {
    onSelectPlace(place)
  }, [onSelectPlace, place])

  const images = useMemo(() => getStoredPlaceImages(place.place), [place.place])
  const isMobileOrTablet = useIsMobileOrTablet()
  const { carouselRef, handleImageChange, handleMouseEnter, handleMouseLeave } = useImageCarouselHoverPreview(images, !isMobileOrTablet)
  const description = useMemo(() => getPlaceDescription(place), [place])
  const coordinates = place.place.point

  return (
    <TileContainer
      onClick={handleSelectPlace}
      aria-label={intl.formatMessage({
        id: 'gGl7SipO3rxqufea0DXP',
        defaultMessage: 'View details for {placeName}',
        description: 'Accessible label for a things to do place tile, which opens that place',
      }, { placeName: place.name })}
      data-testid="things-to-do-place-tile"
    >
      <ImageSection className={clsx({ 'has-no-image': images.length === 0 })} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {itineraryData && onSendMessage && (
          <AddToTripButtonContainer onClick={stopPropagation}>
            <AddToTripAction
              itineraryData={itineraryData}
              onSendMessage={onSendMessage}
              placeName={place.name}
              placeId={place.id}
              coordinates={coordinates}
              variant="filled"
              onAfterAction={onAfterAction}
            />
          </AddToTripButtonContainer>
        )}
        {images.length > 0 && (
          <ImageCarousel
            carouselRef={carouselRef}
            images={images}
            onImageChange={handleImageChange}
            imageParams={PLACE_IMAGE_PARAMS}
            arrowBehaviour="interaction-visible"
            arrowStyle="ghost"
          />
        )}
      </ImageSection>

      <ContentSection direction="vertical" gap={THINGS_TO_DO_TILE_CONTENT_GAP}>
        <Group direction="vertical" gap={4}>
          {displayRegion && (
            <BodyText variant="small" colour="light-foreground-alt">
              {displayRegion}
            </BodyText>
          )}
          <Heading variant="heading5" lineClamp={2}>
            {place.name}
          </Heading>
        </Group>

        {(place.rating !== undefined || place.primaryTypeDisplayName) && (
          <Group direction="horizontal" gap={8} verticalAlign="center">
            {place.rating !== undefined && (
              <GooglePlaceRatingDisplay
                rating={place.rating}
                ratingsCount={place.userRatingCount}
                size="S"
              />
            )}
            {place.rating !== undefined && place.primaryTypeDisplayName && (
              <Divider kind="primary" direction="vertical" />
            )}
            {place.primaryTypeDisplayName && (
              <BodyText variant="small" colour="light-foreground-alt">
                {place.primaryTypeDisplayName}
              </BodyText>
            )}
          </Group>
        )}

        {description && (
          <Group direction="vertical" gap={4}>
            <BodyText variant="medium" lineClamp={THINGS_TO_DO_DESCRIPTION_LINE_CLAMP}>
              {description.text}
            </BodyText>
            {description.attribution && (
              <PlaceDescriptionAttribution attribution={description.attribution} />
            )}
          </Group>
        )}
      </ContentSection>
    </TileContainer>
  )
}

export default memo(ThingsToDoPlaceTile)
